var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import slugify from '@pocketlaw/slugify';
import { DocumentType } from 'shared/domains/apollo/generated/types';
import { localeConfig } from 'shared/domains/languages';
import * as Sentry from 'shared/domains/sentry';
import { useAccount } from 'shared/domains/users';
import { DownloadPleditorDocumentDataDocument } from './DownloadPleditorDocumentData.gql';
import { wrapHtml } from '../utils';
const LOGO_HEIGHT = '12.6mm';
export const useDownloadPleditorDocument = (documentType) => {
    const { data: accountData } = useAccount();
    const { companyId } = accountData;
    const { languageCode } = localeConfig.getActiveLocale();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [query] = useLazyQuery(DownloadPleditorDocumentDataDocument, {
        fetchPolicy: 'network-only',
    });
    const isGenerated = documentType === DocumentType.Composed || documentType === DocumentType.Freetext;
    const getExportUrl = () => {
        const url = process.env.PLEDITOR_EXPORT_CONVERTER_URL;
        if (!url) {
            throw new Error('No Pleditor export converter URL provided');
        }
        return url;
    };
    const getData = (documentId) => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield query({ variables: { documentId, companyId } });
        const { fileSystemDocumentDocxExport, company } = data || {};
        const { body, token } = fileSystemDocumentDocxExport || {};
        const { html = '', config } = body || {};
        const { logoPath, logoUploaded = false } = company || {};
        const attachLogo = Boolean(logoUploaded && logoPath && isGenerated);
        const logoUrl = attachLogo ? `${process.env.CDN_URL}/${logoPath}` : undefined;
        if (!body || !token) {
            throw new Error('Failed to download document');
        }
        return { token, html, config, logoUrl };
    });
    const getCss = () => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL('/public/docx.css', window.location.origin).toString();
        const response = yield fetch(url);
        const css = yield response.text();
        return css;
    });
    const download = (response, documentName) => __awaiter(void 0, void 0, void 0, function* () {
        const name = slugify(documentName);
        const blob = yield response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    });
    const downloadPleditorDocument = (documentId, documentName) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        setError(false);
        try {
            const url = getExportUrl();
            const [css, { html, token, config, logoUrl }] = yield Promise.all([getCss(), getData(documentId)]);
            const header = {
                css: `img.company-logo { max-height: ${LOGO_HEIGHT}; }`,
                html: `<img class="company-logo" src="${logoUrl}" />`,
            };
            const body = {
                css,
                html: wrapHtml(html),
                config: Object.assign(Object.assign({}, config), { document: {
                        language: languageCode,
                    }, headers: {
                        first: logoUrl ? header : undefined,
                    }, base_url: process.env.CDN_URL }),
            };
            const response = yield fetch(url, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    Accept: 'application/docx',
                    'Content-Type': 'application/json',
                    Authorization: token,
                },
            });
            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }
            yield download(response, documentName);
        }
        catch (e) {
            Sentry.captureException(e);
            setError(true);
        }
        finally {
            setLoading(false);
        }
    });
    return { downloadPleditorDocument, loading, error };
};
